import { osTypeValues, type Daemon, type OsType } from '../../lib/api';
import type { AppTarget } from '../../lib/appTarget';
import type { ProjectPermission } from '../../lib/api/projectPermissions';
import {
  PROJECT_LANDING_FALLBACK_PATH,
  findProjectNavRoute,
  resolveProjectLandingPath,
} from '../../lib/projectNavRoutes';

/**
 * 리다이렉트 목적지를 정할 때 쓰는 프로젝트 권한 스냅샷.
 *
 * `isLoading`은 "아직 모름"이다. 이 동안에는 목적지를 정하지 않는다 — 판정 전에 이동하면
 * 권한 없는 화면으로 보내놓고 뒤늦게 거부 화면을 띄우게 된다.
 */
export type ProjectAccessSnapshot = {
  isLoading: boolean;
  can: (permission: ProjectPermission) => boolean;
};

const workspaceRouteFallbackMap: Record<string, string> = {
  '/plans/new': '/plans',
  '/conventions/new': '/conventions',
};

export const shouldSyncWorkspaceQuery = (pathname: string) => {
  return !(
    pathname === '/' ||
    pathname === '/home' ||
    pathname === '/notifications' ||
    // 관리자 콘솔(/system/*, /feedbacks)은 프로젝트 종속이 아니므로 워크스페이스 쿼리 동기화/리디렉션 대상에서 제외한다.
    // (isAdminMode 정의와 동일 범위 — 직접 URL 진입 시 프로젝트 마지막 경로로 튕기는 문제 방지)
    pathname.startsWith('/system') ||
    pathname === '/feedbacks' ||
    pathname.startsWith('/auth') ||
    pathname.startsWith('/cli') ||
    pathname.startsWith('/go') ||
    pathname.startsWith('/reset-password') ||
    pathname.startsWith('/invitations') ||
    pathname.startsWith('/share') ||
    pathname === '/terms' ||
    pathname === '/privacy' ||
    // 계정 범위 자산(러너·개인 모델) 호환 경로 — 프로젝트 컨텍스트를 붙이지 않고 모달로 넘긴다.
    pathname.startsWith('/member-runner-models')
  );
};

export const isShelllessRoute = (pathname: string) => {
  return (
    pathname.startsWith('/auth') ||
    pathname === '/' ||
    pathname.startsWith('/cli') ||
    pathname.startsWith('/go') ||
    pathname.startsWith('/reset-password') ||
    pathname.startsWith('/invitations') ||
    pathname.startsWith('/share') ||
    pathname === '/terms' ||
    pathname === '/privacy'
  );
};

/**
 * 빌드 타깃을 반영한 셸리스 판정.
 *
 * web의 `/`는 공개 랜딩이라 셸리스지만, Portal의 `/`는 인증이 필요한 Portal 홈이다.
 * 셸리스로 처리하면 AuthGuard·AppShellContextProvider 없이 렌더되어 MinimalShell이
 * 깨지고 미인증 이동도 일어나지 않으므로, Portal 빌드에서는 `/`를 셸리스에서 뺀다.
 */
export const isShelllessRouteForTarget = (pathname: string, target: AppTarget) => {
  return isShelllessRoute(pathname) && !(target === 'portal' && pathname === '/');
};

const buildWorkspaceHref = (pathname: string, searchParams: URLSearchParams, teamId: string, projectId: string) => {
  const nextParams = new URLSearchParams(searchParams.toString());
  nextParams.set('teamId', teamId);
  nextParams.set('projectId', projectId);
  const query = nextParams.toString();
  return query.length > 0 ? `${pathname}?${query}` : pathname;
};

/**
 * 프로젝트 진입 목적지 href. 랜딩 경로는 호출부가 `resolveProjectLandingPath`로 정해 넘긴다.
 *
 * `null`(접근 가능한 라우트 0개)이면 대시보드로 보내 접근 거부 UI가 뜨게 한다. 게이트는
 * 리다이렉트하지 않으므로 루프가 생기지 않는다.
 */
export const buildProjectLandingWorkspaceHref = (landingPath: string | null, teamId: string, projectId: string) => {
  return buildWorkspaceHref(landingPath ?? PROJECT_LANDING_FALLBACK_PATH, new URLSearchParams(), teamId, projectId);
};

/**
 * 기억된 프로젝트 경로(`getWorkspaceProjectRoute`)를 현재 permission으로 검증한다.
 *
 * 역할이 강등되면 기억 경로가 그대로 막다른 길이 된다. 내비 라우트에 해당하고 권한이 없으면
 * 권한 기반 랜딩으로 대체한다. 상세 화면처럼 내비 라우트가 아닌 경로는 그대로 통과시킨다 —
 * 내비 permission으로 판정하면 오탐으로 정상 복귀를 막는다.
 */
export const resolveRememberedProjectRoute = (
  route: string,
  can: (permission: ProjectPermission) => boolean,
): string => {
  const [pathnamePart = ''] = route.split('?');
  if (pathnamePart.length === 0) {
    return resolveProjectLandingPath(can) ?? PROJECT_LANDING_FALLBACK_PATH;
  }

  const navRoute = findProjectNavRoute(pathnamePart);
  if (!navRoute || can(navRoute.permission)) {
    return route;
  }

  return resolveProjectLandingPath(can) ?? PROJECT_LANDING_FALLBACK_PATH;
};

export const getWorkspaceSwitchBasePath = (pathname: string) => {
  return workspaceRouteFallbackMap[pathname] ?? pathname;
};

export const isWorkspaceRouteRememberable = (pathname: string) => {
  return shouldSyncWorkspaceQuery(pathname) && !pathname.endsWith('/new');
};

export const buildWorkspaceHrefFromStoredRoute = (route: string, teamId: string, projectId: string) => {
  const [pathnamePart, queryPart = ''] = route.split('?');
  return buildWorkspaceHref(
    pathnamePart || PROJECT_LANDING_FALLBACK_PATH,
    new URLSearchParams(queryPart),
    teamId,
    projectId,
  );
};

export const getWorkspaceRedirectHref = ({
  pathname,
  searchParams,
  preferredRoute,
  nextTeamId,
  nextProjectId,
  currentTeamId,
  currentProjectId,
  access,
}: {
  pathname: string;
  searchParams: URLSearchParams;
  preferredRoute?: string | null;
  nextTeamId: string;
  nextProjectId: string;
  currentTeamId: string;
  currentProjectId: string;
  access: ProjectAccessSnapshot;
}) => {
  if (!nextTeamId || !nextProjectId) {
    return null;
  }
  if (currentTeamId === nextTeamId && currentProjectId === nextProjectId) {
    return null;
  }

  // 같은 프로젝트로의 진입(예: 딥링크에 teamId만 누락)이면 preferredRoute(프로젝트의 마지막 기억 경로)로
  // 튕기지 말고 현재 경로를 유지한 채 teamId/projectId만 보강한다. preferredRoute 리다이렉트는
  // 프로젝트가 실제로 바뀌는 워크스페이스 전환에만 적용한다.
  if (currentProjectId === nextProjectId) {
    return buildWorkspaceHref(pathname, searchParams, nextTeamId, nextProjectId);
  }

  // 목적지를 정하려면 권한을 알아야 한다. 판정 전이면 이동하지 않고 기다린다(판정 후 다시 계산된다).
  // 기억 경로가 있든 없든 같다 — 한쪽만 기다리면 "기억 경로가 있을 때만 고쳐지는" 절반짜리 수정이 된다.
  if (access.isLoading) {
    return null;
  }

  if (preferredRoute) {
    return buildWorkspaceHrefFromStoredRoute(
      resolveRememberedProjectRoute(preferredRoute, access.can),
      nextTeamId,
      nextProjectId,
    );
  }

  // 기억 경로가 없는 첫 진입(저장 컨텍스트/팀의 첫 프로젝트로 자동 선택 등)도 현재 화면을 그대로 끌고 간다.
  // 새 프로젝트에서 그 화면을 볼 수 없으면 막다른 길이므로 권한 기반 랜딩으로 대체한다.
  // 내비 라우트가 아닌 경로(상세 화면 등)는 그대로 통과시킨다 — 내비 permission으로 판정하면 오탐이다.
  const nextPathname = getWorkspaceSwitchBasePath(pathname);
  const nextNavRoute = findProjectNavRoute(nextPathname);
  if (nextNavRoute && !access.can(nextNavRoute.permission)) {
    return buildProjectLandingWorkspaceHref(resolveProjectLandingPath(access.can), nextTeamId, nextProjectId);
  }

  const nextSearchParams = nextPathname === pathname ? searchParams : new URLSearchParams();

  return buildWorkspaceHref(nextPathname, nextSearchParams, nextTeamId, nextProjectId);
};

export const getCompatibleDaemonsForAgentConfig = <T extends Pick<Daemon, 'osType'>>(
  daemons: T[],
  agentConfigOsType: OsType | null | undefined,
) => {
  if (!agentConfigOsType || !osTypeValues.includes(agentConfigOsType)) {
    return daemons;
  }

  return daemons.filter((daemon) => daemon.osType === null || daemon.osType === agentConfigOsType);
};
